import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilePanel from "../../../components/FilePanel";
import {
  getAvatarUrl,
  readStoredAvatarSeed,
} from "../../../components/navigation/avatarPresets";
import { getCommentAuthorName } from "../../characterization-2/core/utils/characterizationComments";
import { formatCommentDate } from "../../characterization-2/core/utils/dateFormatUtils";

const getCurrentUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?._id || user?.id || null;
  } catch {
    return null;
  }
};

const resolveAuthorAvatarUrl = (author) => {
  const currentUserId = getCurrentUserId();
  const authorId = author?._id || author?.id || null;
  if (currentUserId && authorId && String(currentUserId) === String(authorId)) {
    return getAvatarUrl(readStoredAvatarSeed());
  }
  const seed =
    authorId ||
    getCommentAuthorName(author) ||
    author?.email ||
    author?.firstName ||
    "Alex";
  return getAvatarUrl(String(seed));
};

const buildCommentEntries = (comments) =>
  (comments || [])
    .map((comment) => ({
      key: comment.time || comment.text,
      text: comment.text,
      author: comment.author,
      authorName: getCommentAuthorName(comment.author),
      time: comment.time,
      raw: comment,
    }))
    .filter((comment) => comment.text)
    .sort((first, second) => new Date(second.time) - new Date(first.time));

const buildRevisionEntries = (reviewComments, t) =>
  (reviewComments || [])
    .map((review) => ({
      key: review.id || review.reviewTime,
      time: review.reviewTime,
      oldLabel: review?.oldCls ? t(review.oldCls) : t(review.cls),
      newLabel: t(review.reviewCls || review.cls),
      comment: review.comment,
      status: review.reviewStatus || "pending",
    }))
    .sort((first, second) => new Date(second.time) - new Date(first.time));

const CommentTrashIcon = () => (
  <svg
    width="11"
    height="12"
    viewBox="0 0 11 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M2 12C1.63333 12 1.31944 11.8694 1.05833 11.6083C0.797222 11.3472 0.666667 11.0333 0.666667 10.6667V2H0V0.666667H3.33333V0H7.33333V0.666667H10.6667V2H10V10.6667C10 11.0333 9.86945 11.3472 9.60833 11.6083C9.34722 11.8694 9.03333 12 8.66667 12H2ZM8.66667 2H2V10.6667H8.66667V2ZM3.33333 9.33333H4.66667V3.33333H3.33333V9.33333ZM6 9.33333H7.33333V3.33333H6V9.33333Z"
      fill="#222222"
    />
  </svg>
);

/**
 * Follow-up tab: comments, material revision history, imported documents.
 */
export const ActivityDetailFollowUpTab = ({
  comments,
  reviewComments,
  filePanelProps,
  onAddComment,
  onEditComment,
  onDeleteComment,
}) => {
  const { t, i18n } = useTranslation();
  const [isNotesOpen, setIsNotesOpen] = useState(true);
  const [isRevisionsOpen, setIsRevisionsOpen] = useState(true);
  const [outputClient, setOutputClient] = useState({});

  const entries = useMemo(() => buildCommentEntries(comments), [comments]);
  const revisions = useMemo(
    () => buildRevisionEntries(reviewComments, t),
    [reviewComments, t],
  );

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setIsNotesOpen((open) => !open)}
          className="flex items-center gap-2 border-0 bg-transparent p-0 cursor-pointer text-left"
          aria-expanded={isNotesOpen}
        >
          <h2 className="char-section-header-title !mb-0">
            {t("Comments")} ({entries.length})
          </h2>
          <i
            className={`fas fa-chevron-${isNotesOpen ? "up" : "down"} text-sm text-[#6B7280]`}
            aria-hidden
          />
        </button>
        <button
          type="button"
          onClick={onAddComment}
          className="char-button char-button-primary"
        >
          {t("Add a comment")}
        </button>
      </div>

      {isNotesOpen ? (
        <div className="mt-3 flex flex-col gap-3">
          {entries.length === 0 ? (
            <article className="rounded-xl border border-gray-300 bg-white p-4">
              <p className="m-0 text-sm text-[#6B7280]">{t("No comments")}</p>
            </article>
          ) : (
            entries.map((entry) => {
              const authorName = entry.authorName || t("Unknown author");
              return (
                <article
                  key={entry.key}
                  className="overflow-hidden rounded-xl border border-gray-300 bg-white"
                >
                  <div className="char-comment-item">
                    <div className="char-comment-item-header">
                      <span className="char-comment-item-header-date !text-[16px]">
                        {formatCommentDate(entry.time, i18n.language)}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          className="char-comment-item-header-edit"
                          onClick={() => onEditComment?.(entry.raw)}
                        >
                          {t("Edit")}
                        </button>
                        <button
                          type="button"
                          aria-label={t("Delete")}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#D3D3D3] bg-white cursor-pointer hover:bg-[#F8F8F8]"
                          onClick={() => onDeleteComment?.(entry.raw)}
                        >
                          <CommentTrashIcon />
                        </button>
                      </div>
                    </div>
                    <p className="char-comment-item-content !text-[#5E5E5E]">
                      {entry.text}
                    </p>
                    <div className="char-comment-item-footer">
                      <div className="char-comment-item-footer-avatar !h-8 !w-8 overflow-hidden bg-transparent">
                        <img
                          src={resolveAuthorAvatarUrl(entry.author)}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <span className="char-comment-item-footer-author">
                        {authorName}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      ) : null}

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setIsRevisionsOpen((open) => !open)}
          className="flex items-center gap-2 border-0 bg-transparent p-0 cursor-pointer text-left"
          aria-expanded={isRevisionsOpen}
        >
          <h2 className="char-section-header-title !mb-0">
            {t("Revision history")} ({revisions.length})
          </h2>
          <i
            className={`fas fa-chevron-${isRevisionsOpen ? "up" : "down"} text-sm text-[#6B7280]`}
            aria-hidden
          />
        </button>
      </div>

      {isRevisionsOpen ? (
        <div className="mt-3 flex flex-col gap-3">
          {revisions.length === 0 ? (
            <article className="rounded-xl border border-gray-300 bg-white p-4">
              <p className="m-0 text-sm text-[#6B7280]">
                {t("No material corrections")}
              </p>
            </article>
          ) : (
            revisions.map((revision) => (
              <article
                key={revision.key}
                className="overflow-hidden rounded-xl border border-gray-300 bg-white p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-[#6B7280]">
                    {formatCommentDate(revision.time, i18n.language)}
                  </span>
                  <span className="text-sm font-medium text-[#222222]">
                    {t(revision.status)}
                  </span>
                </div>
                <p className="mt-2 mb-0 text-sm text-[#222222]">
                  <span className="font-bold">
                    {t("Material correction")}:{" "}
                  </span>
                  <span className="line-through text-[#6B7280]">
                    {revision.oldLabel}
                  </span>
                  <span>{` → ${revision.newLabel}`}</span>
                </p>
                {revision.comment ? (
                  <p className="mt-2 mb-0 text-sm text-[#5E5E5E]">
                    {revision.comment}
                  </p>
                ) : null}
              </article>
            ))
          )}
        </div>
      ) : null}

      <article className="mt-10 overflow-hidden rounded-xl border border-gray-300 bg-white">
        <FilePanel
          {...filePanelProps}
          embedded
          presentation="followUp"
          outputClient={outputClient}
          setOutputClient={setOutputClient}
        />
      </article>
    </div>
  );
};
